import React from 'react';
import { Dialog, DialogContent, DialogActions, Typography, Button, Box } from '@mui/material';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export default function ModernAlertDialog({
  open,
  handleClose,
  title,
  msg,
  iconType,
  confirmLabel = "Ok",
  cancelLabel = "Cancelar",
  showCancel = false,
  onConfirm,
  onCancel,
}) {
  const primaryColor = "rgb(255, 49, 0)";
  const lightText = "#f2f2f2";
  const fullText = `${title || ""} ${Array.isArray(msg) ? msg.join(" ") : msg || ""}`.toLowerCase();

  const resolveIconByContext = () => {
    if (iconType) return iconType;
    if (fullText.includes("whatsapp")) return "whatsapp";
    if (fullText.includes("e-mail") || fullText.includes("email")) return "email";
    if (
      fullText.includes("exclu") ||
      fullText.includes("delet") ||
      fullText.includes("remov")
    ) {
      return "delete";
    }
    if (
      fullText.includes("erro") ||
      fullText.includes("falha") ||
      fullText.includes("nao foi possivel")
    ) {
      return "error";
    }
    if (
      fullText.includes("sucesso") ||
      fullText.includes("aprovad") ||
      fullText.includes("salv") ||
      fullText.includes("cadastrad") ||
      fullText.includes("atualizad") ||
      fullText.includes("enviado")
    ) {
      return "success";
    }
    if (
      fullText.includes("obrigat") ||
      fullText.includes("atencao")
    ) {
      return "warning";
    }
    return "info";
  };

  const resolvedIcon = resolveIconByContext();
  const iconMap = {
    delete: { icon: <DeleteOutlineIcon sx={{ fontSize: 28, color: "#d32f2f" }} />, bg: "rgba(211, 47, 47, 0.1)" },
    email: { icon: <SendIcon sx={{ fontSize: 28, color: "#ff6a00" }} />, bg: "rgba(255, 106, 0, 0.1)" },
    whatsapp: { icon: <WhatsAppIcon sx={{ fontSize: 28, color: "#25d366" }} />, bg: "rgba(37, 211, 102, 0.14)" },
    success: { icon: <CheckCircleOutlineIcon sx={{ fontSize: 28, color: "#2e7d32" }} />, bg: "rgba(46, 125, 50, 0.12)" },
    error: { icon: <ErrorOutlineIcon sx={{ fontSize: 28, color: "#d32f2f" }} />, bg: "rgba(211, 47, 47, 0.1)" },
    warning: { icon: <WarningAmberIcon sx={{ fontSize: 28, color: "#ff3100" }} />, bg: "rgba(255, 49, 0, 0.1)" },
    info: { icon: <InfoOutlinedIcon sx={{ fontSize: 28, color: "#ff3100" }} />, bg: "rgba(255, 49, 0, 0.1)" },
  };
  const iconConfig = iconMap[resolvedIcon] || iconMap.info;

  const handleConfirmClick = () => {
    if (typeof onConfirm === "function") {
      onConfirm();
      return;
    }
    if (typeof handleClose === "function") {
      handleClose();
    }
  };

  const handleCancelClick = () => {
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    if (typeof handleClose === "function") {
      handleClose();
    }
  };
  return (
    <Dialog
      open={open}
      onClose={handleCancelClick}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      // Estilização 
      PaperProps={{
        sx: {
          borderRadius: '24px', 
          padding: '20px',
          maxWidth: '620px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0px 10px 40px rgba(1, 36, 67, 0.15)', 
        },
      }}
    >
      {/* Cabeçalho com Ícone e Título */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
        {/* Círculo de fundo para o ícone  */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: iconConfig.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {iconConfig.icon}
        </Box>

        <Typography 
          id="alert-dialog-title" 
          variant="h5" 
          sx={{ 
            color: '#012443', 
            fontWeight: 700,
            fontFamily: 'sans-serif' 
          }}
        >
          {title || "Atenção"}
        </Typography>
      </Box>

      {/* Conteúdo da Mensagem (Renderizando o Array) */}
      <DialogContent sx={{ p: 2 }}>
        <Box id="alert-dialog-description">
          {Array.isArray(msg) && msg.length > 0 ? (
            msg.map((item, index) => (
              <Typography 
                key={index} 
                variant="body1" 
                sx={{ 
                  color: '#555', 
                  fontSize: '1rem', 
                  marginBottom: '8px',
                  lineHeight: 1.5 
                }}
              >
                {item}
              </Typography>
            ))
          ) : (
            // Fallback caso venha uma string simples
            <Typography variant="body1" sx={{ color: '#555' }}>
              {msg}
            </Typography>
          )}
        </Box>
      </DialogContent>

      {/* Ações  */}
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        {showCancel && (
          <Button
            onClick={handleCancelClick}
            variant="outlined"
            sx={{
              borderColor: primaryColor,
              color: primaryColor,
              borderRadius: '50px',
              padding: '10px 32px',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: primaryColor,
                color: lightText,
                borderColor: primaryColor,
              },
            }}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          onClick={handleConfirmClick}
          variant="contained"
          disableElevation
          sx={{
            backgroundColor: primaryColor, 
            color: lightText,
            borderRadius: '50px', 
            padding: '10px 40px',
            textTransform: 'none', 
            fontSize: '1rem',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: lightText,
              color: primaryColor,
              borderColor: primaryColor,
            },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
